import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';

import type { ExchangeReplacementItem, ExchangeReturnDisposition } from './types';
import { multiWordMatch } from '@/utils/multiWordMatch';

export interface ReplacementPaneProps {
  linFilter: string | null;
  baseNsnFilter?: string | null;
  parentCatalogIdFilter?: string | null;
  catalogIdFilter?: string | null;
  baseNomenclatureFilter?: string | null;
  replacementItems: ExchangeReplacementItem[];
  pairedReplacementId?: string;
  onStageReplacement: (replacementId: string, disposition: ExchangeReturnDisposition) => void;
  isLoading?: boolean;
}

const normalize = (value: string) => value.trim().toLowerCase();

export const ReplacementPane = ({
  linFilter,
  baseNsnFilter,
  parentCatalogIdFilter,
  catalogIdFilter,
  baseNomenclatureFilter,
  replacementItems,
  pairedReplacementId,
  onStageReplacement,
  isLoading = false,
}: ReplacementPaneProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReplacementId, setSelectedReplacementId] = useState<string | null>(
    pairedReplacementId ?? null,
  );

  useEffect(() => {
    setSelectedReplacementId(pairedReplacementId ?? null);
  }, [pairedReplacementId, linFilter, baseNsnFilter, parentCatalogIdFilter, catalogIdFilter, baseNomenclatureFilter]);

  const normalizedQuery = normalize(searchTerm);

  const scopedReplacements = useMemo(() => {
    if (!parentCatalogIdFilter && !linFilter && !catalogIdFilter) {
      return [];
    }

    let scoped: ExchangeReplacementItem[];
    if (parentCatalogIdFilter) {
      // Show all sized variants that share the same parent catalog.
      // Each nomenclature family has its own parent, so parentCatalogId
      // is sufficient to scope to the correct family.
      scoped = replacementItems.filter((item) => item.parentCatalogId === parentCatalogIdFilter);
    } else if (catalogIdFilter) {
      // Issued item has a catalogId but no parentCatalogId.  Look up the
      // matching replacement entry — if it belongs to a sized family (has a
      // parentCatalogId), expand to show all siblings under that parent so
      // the user can pick any available size.
      const exactMatch = replacementItems.find((item) => item.id === catalogIdFilter);
      if (exactMatch?.parentCatalogId) {
        scoped = replacementItems.filter(
          (item) => item.parentCatalogId === exactMatch.parentCatalogId,
        );
      } else {
        scoped = exactMatch ? [exactMatch] : [];
      }
    } else {
      scoped = replacementItems.filter((item) => item.lin === linFilter);

      // If any item in this LIN group is a sized variant (has a parent),
      // then we should only show sized variants.
      // This hides the "parent" container item if it exists in the list.
      const hasSizedVariants = scoped.some((item) => !!item.parentCatalogId);
      if (hasSizedVariants) {
        scoped = scoped.filter((item) => !!item.parentCatalogId);
      }
    }

    if (!normalizedQuery) {
      return scoped;
    }

    return scoped.filter((item) => {
      const searchable = `${item.name} ${item.lin} ${item.nsn}`;
      return multiWordMatch(searchable, normalizedQuery);
    });
  }, [linFilter, parentCatalogIdFilter, catalogIdFilter, normalizedQuery, replacementItems]);

  const selectedRow = scopedReplacements.find((item) => item.id === selectedReplacementId) ?? null;

  const handleAddToCart = (disposition: ExchangeReturnDisposition) => {
    if (!selectedRow || selectedRow.stock <= 0) {
      return;
    }

    onStageReplacement(selectedRow.id, disposition);
  };

  if (!linFilter && !parentCatalogIdFilter && !catalogIdFilter) {
    return (
      <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <InventoryIcon color="disabled" />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Select an issued item
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Replacement options appear once you focus an issued item in the left pane.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ height: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="stretch">
        <TextField
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search replacements"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
          disabled={isLoading}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="stretch"
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<ChevronRightIcon />}
            onClick={() => handleAddToCart('recoverable')}
            disabled={isLoading || !selectedRow || selectedRow.stock <= 0}
            sx={{ flexGrow: 1 }}
          >
            Serviceable
          </Button>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            endIcon={<ChevronRightIcon />}
            onClick={() => handleAddToCart('non_recoverable')}
            disabled={isLoading || !selectedRow || selectedRow.stock <= 0}
            sx={{ flexGrow: 1 }}
          >
            Non-Serviceable
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              Loading replacement options...
            </Typography>
          </Stack>
        ) : scopedReplacements.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              No replacement items available for this NSN family.
            </Typography>
          </Stack>
        ) : (
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table stickyHeader size="small" aria-label="Replacement options">
              <TableHead>
                <TableRow>
                  <TableCell>LIN</TableCell>
                  <TableCell>NSN</TableCell>
                  <TableCell>Nomenclature</TableCell>
                  <TableCell align="right">Stock</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scopedReplacements.map((item) => {
                  const isSelected = item.id === selectedReplacementId;
                  const isOutOfStock = item.stock <= 0;

                  const handleRowClick = () => {
                    if (isOutOfStock) {
                      return;
                    }
                    setSelectedReplacementId(item.id);
                  };

                  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                    if (event.key !== 'Enter' && event.key !== ' ') {
                      return;
                    }
                    event.preventDefault();
                    handleRowClick();
                  };

                  return (
                    <TableRow
                      key={item.id}
                      hover={!isOutOfStock}
                      selected={isSelected}
                      onClick={handleRowClick}
                      onKeyDown={handleKeyDown}
                      tabIndex={isOutOfStock ? -1 : 0}
                      aria-disabled={isOutOfStock}
                      sx={{
                        cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                        opacity: isOutOfStock ? 0.5 : 1,
                        '&.Mui-selected': (theme) => ({
                          backgroundColor: theme.palette.action.selected,
                        }),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{item.lin}</TableCell>
                      <TableCell>{item.nsn}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{item.stock}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
};
